import { useEffect, useState } from 'react';
import { useIsMobile } from '../ui/useMediaQuery';
import {
  DRAW_TOOL_LABEL,
  NOTE_COLORS,
  isCustomPaper,
  type DrawTool,
  type Item,
  type NoteItem,
  type TitleItem,
  type ImageItem,
  type ShapeItem,
  type StrokeItem,
  type RichTextItem,
  type LinkItem,
  type ShapeKind,
  type FileItem,
  type FrameItem,
  type TableItem,
  type TodoItem,
  type BoardCardItem,
  type BoardCardLayout,
  type BoardCardFit,
  type SheetItem,
  type ConnectorItem,
} from '../doc/schema';
import { ConnectorFields } from './connector/ConnectorFields';
import { openAsset, storeImageAsset } from './assets/localAssets';
import { isImageFile, pickImageFiles } from './importImages';
import { enqueueUpload } from '../sync/uploadQueue';
import { openBoardCard, setBoardPickerTarget } from './boardCard';
import { useBoardLookup } from './items/boardSummary';
import { usePanel } from './panels';
import { IconBoard, IconLayers, IconPen, IconScissors } from '../ui/icons';
import { enterPaint, exportSheetPng, flattenSheet } from './paint/session';
import { sheetLabel } from './paint/presets';
import { formatBytes } from './items/FileItem';
import { ColorPicker, OpacitySlider, SizePicker } from '../ui/ColorPicker';
import { TextStyleFields } from './text/TextStyleFields';
import { textStyleOf, textStylePatch } from './text/textStyle';
import { refreshLinkPreview } from './actions';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../doc/zorder';
import {
  IconAlignB,
  IconAlignCX,
  IconAlignCY,
  IconAlignL,
  IconAlignR,
  IconAlignT,
  IconBack,
  IconBackward,
  IconDistH,
  IconDistV,
  IconDuplicate,
  IconExternal,
  IconForward,
  IconFront,
  IconGrid,
  IconGroup,
  IconListBullet,
  IconLock,
  IconTrash,
  IconUngroup,
  IconUnlock,
} from '../ui/icons';
import {
  alignSelection,
  arrangeGrid,
  distributeSelection,
  groupSelection,
  normalizeSelection,
  toggleLockSelection,
  ungroupSelection,
  type AlignMode,
} from './arrange';
import { deleteSelection, duplicateSelection } from './actions';
import { useBoard, useBoardMeta, useItemsVersion, usePane, usePaneStore, useYMap } from './context';
import { HANDLE_TITLE, HandleControls, useFloatingCard } from './floating';
import { flash } from './flash';
import { lookOfNote, NOTE_LOOK_KEY, parseNoteLook, resetBoardNoteLook, sameNoteLook, setBoardNoteLook } from './noteLook';
import { copyNotesAsList } from './noteList';
import type { LayerDef } from '../doc/schema';

/** Properties panel for the current selection. Re-renders on document changes, which is fine for a panel. */
export function Inspector() {
  const board = useBoard();
  const pane = usePaneStore();
  const selection = usePane((s) => s.selection);
  useItemsVersion();
  const layers = useYMap<LayerDef>(board.layers);
  const mobile = useIsMobile();
  // On phones the inspector is a sheet above the toolbar, collapsed to its header until tapped.
  const [open, setOpen] = useState(false);
  // On desktop the header is a grab handle: the inspector can float anywhere in the pane.
  const card = useFloatingCard<HTMLElement>('inspector');

  const items: Item[] = [];
  for (const id of selection) {
    const item = board.index.get(id);
    if (item) items.push(item);
  }
  if (!items.length) return null;
  const single = items.length === 1 ? items[0] : undefined;
  const ids = new Set(items.map((i) => i.id));
  const ctx = { board, pane };
  const allLocked = items.every((i) => i.locked);
  // strokes always sit on their tool's ink layer; the layer picker applies to the rest
  const movable = items.filter((i) => i.type !== 'stroke');
  const layerIds = new Set(movable.map((i) => (i.layerId && layers[i.layerId] ? i.layerId : '')));
  const layerValue = layerIds.size === 1 ? [...layerIds][0]! : '__mixed__';
  const strokes = items.filter((i): i is StrokeItem => i.type === 'stroke');
  const notes = items.filter((i): i is NoteItem => i.type === 'note');
  const connectors = items.filter((i): i is ConnectorItem => i.type === 'connector');
  const inkLayers = new Set(strokes.map((i) => (i.layerId && layers[i.layerId]?.name) || 'Ink'));
  const anyGrouped = items.some((i) => i.groupId);

  const collapsed = mobile && !open;

  return (
    <aside
      ref={card.ref}
      className={`inspector${collapsed ? ' is-collapsed' : ''}${card.floating ? ' is-floating' : ''}`}
      style={card.style}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={`insp-head${mobile ? '' : ' panel-handle'}`}
        title={mobile ? undefined : HANDLE_TITLE}
        onClick={mobile ? () => setOpen((o) => !o) : undefined}
        role={mobile ? 'button' : undefined}
        {...card.handleProps}
      >
        <span>{single ? single.type : `${items.length} items`}</span>
        {!mobile && <HandleControls card={card} />}
        {mobile && (
          <span className="insp-quick" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-icon" title="Duplicate" aria-label="Duplicate" onClick={() => void duplicateSelection(ctx)}>
              <IconDuplicate />
            </button>
            <button type="button" className="btn btn-icon danger" title="Delete" aria-label="Delete" disabled={allLocked} onClick={() => deleteSelection(ctx)}>
              <IconTrash />
            </button>
            <span className="insp-caret" aria-hidden onClick={() => setOpen((o) => !o)}>
              {open ? '▾' : '▴'}
            </span>
          </span>
        )}
      </div>
      {!collapsed && (
      <div className="insp-body">
        {single && single.type !== 'connector' && <GeometryFields item={single} />}
        {connectors.length === items.length && <ConnectorFields items={connectors} />}
        {single?.type === 'note' && <NoteFields item={single} />}
        {single?.type === 'title' && <TitleFields item={single} />}
        {single?.type === 'image' && <ImageFields item={single} />}
        {single?.type === 'shape' && <ShapeFields item={single} />}
        {strokes.length === items.length && <StrokeFields items={strokes} />}
        {(single?.type === 'richtext' || single?.type === 'table' || single?.type === 'todo') && <RichTextFields item={single} />}
        {single?.type === 'file' && <FileFields item={single} />}
        {single?.type === 'link' && <LinkFields item={single} />}
        {single?.type === 'frame' && <FrameFields item={single} />}
        {single?.type === 'boardcard' && <BoardCardFields item={single} />}
        {single?.type === 'sheet' && <SheetFields item={single} />}
        {notes.length >= 2 && (
          <>
            <div className="insp-label">Sticky notes ({notes.length})</div>
            <div className="btn-row">
              <button
                type="button"
                className="btn"
                title="Copy the notes to the clipboard as a bullet list, one bullet per note, in reading order (rows from the top, left to right)"
                onClick={(e) => void copyNotesAsList(notes, e.currentTarget)}
              >
                <IconListBullet /> Copy as list
              </button>
            </div>
          </>
        )}

        {items.length >= 2 && (
          <>
            <div className="insp-label">Arrange</div>
            <div className="insp-grid">
              {(
                [
                  ['left', IconAlignL, 'Align left edges'],
                  ['centerX', IconAlignCX, 'Align horizontal centres'],
                  ['right', IconAlignR, 'Align right edges'],
                  ['top', IconAlignT, 'Align top edges'],
                  ['centerY', IconAlignCY, 'Align vertical centres'],
                  ['bottom', IconAlignB, 'Align bottom edges'],
                ] as [AlignMode, () => React.JSX.Element, string][]
              ).map(([mode, Icon, title]) => (
                <button key={mode} type="button" className="btn" title={title} onClick={() => alignSelection(ctx, mode)}>
                  <Icon />
                </button>
              ))}
            </div>
            <div className="btn-row">
              <button type="button" className="btn" title="Distribute horizontally (3+)" disabled={items.length < 3} onClick={() => distributeSelection(ctx, 'x')}>
                <IconDistH />
              </button>
              <button type="button" className="btn" title="Distribute vertically (3+)" disabled={items.length < 3} onClick={() => distributeSelection(ctx, 'y')}>
                <IconDistV />
              </button>
              <button type="button" className="btn" title="Arrange in a grid" onClick={() => arrangeGrid(ctx)}>
                <IconGrid />
              </button>
            </div>
            <div className="insp-label">Same size as first selected</div>
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'width')}>
                Width
              </button>
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'height')}>
                Height
              </button>
              <button type="button" className="btn" onClick={() => normalizeSelection(ctx, 'longest')}>
                Fit
              </button>
            </div>
          </>
        )}

        {inkLayers.size > 0 && (
          <>
            <div className="insp-label">Layer</div>
            <div className="insp-muted">{[...inkLayers].join(', ')} (ink layer, set by the tool)</div>
          </>
        )}
        {movable.length > 0 && Object.values(layers).some((l) => !l.ink) && (
          <>
            <div className="insp-label">Layer</div>
            <select
              className="font-select"
              value={layerValue}
              onChange={(e) => board.moveSelectionToLayer(e.target.value === '' ? null : e.target.value, [...ids])}
            >
              {layerValue === '__mixed__' && <option value="__mixed__">(mixed)</option>}
              <option value="">Default</option>
              {Object.entries(layers)
                .filter(([, l]) => !l.ink)
                .sort((a, b) => b[1].order - a[1].order)
                .map(([id, l]) => (
                  <option key={id} value={id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </>
        )}

        <div className="insp-label">Order</div>
        <div className="btn-row">
          <button type="button" className="btn" title="Bring to front (Ctrl+])" onClick={() => bringToFront(board, ids)}>
            <IconFront />
          </button>
          <button type="button" className="btn" title="Bring forward (])" onClick={() => bringForward(board, ids)}>
            <IconForward />
          </button>
          <button type="button" className="btn" title="Send backward ([)" onClick={() => sendBackward(board, ids)}>
            <IconBackward />
          </button>
          <button type="button" className="btn" title="Send to back (Ctrl+[)" onClick={() => sendToBack(board, ids)}>
            <IconBack />
          </button>
        </div>

        <div className="btn-row">
          {items.length >= 2 && (
            <button type="button" className="btn" title="Group (Ctrl+G)" onClick={() => groupSelection(ctx)}>
              <IconGroup /> Group
            </button>
          )}
          {anyGrouped && (
            <button type="button" className="btn" title="Ungroup (Ctrl+Shift+G)" onClick={() => ungroupSelection(ctx)}>
              <IconUngroup /> Ungroup
            </button>
          )}
          <button type="button" className={`btn${allLocked ? ' is-active' : ''}`} title="Lock / unlock (Ctrl+L)" onClick={() => toggleLockSelection(ctx)}>
            {allLocked ? <IconUnlock /> : <IconLock />} {allLocked ? 'Unlock' : 'Lock'}
          </button>
        </div>
        <div className="btn-row">
          <button type="button" className="btn" title="Duplicate (Ctrl+D)" onClick={() => void duplicateSelection(ctx)}>
            <IconDuplicate /> Duplicate
          </button>
          <button type="button" className="btn danger" title="Delete (Del)" disabled={allLocked} onClick={() => deleteSelection(ctx)}>
            <IconTrash /> Delete
          </button>
        </div>
      </div>
      )}
    </aside>
  );
}

function GeometryFields({ item }: { item: Item }) {
  const board = useBoard();
  const set = (key: 'x' | 'y' | 'w' | 'h' | 'rotation') => (v: number) => board.updateItem(item.id, { [key]: v });
  return (
    <>
      <div className="insp-row">
        <NumberField label="X" value={item.x} onCommit={set('x')} />
        <NumberField label="Y" value={item.y} onCommit={set('y')} />
      </div>
      <div className="insp-row">
        <NumberField label="W" value={item.w} min={1} onCommit={set('w')} />
        <NumberField label="H" value={item.h} min={1} onCommit={set('h')} />
      </div>
      <div className="insp-row">
        <NumberField label="R" value={item.rotation} step={1} onCommit={set('rotation')} />
      </div>
    </>
  );
}

function NoteFields({ item }: { item: NoteItem }) {
  const board = useBoard();
  // the board default lives in meta, which item changes do not re-render
  const stored = useYMap<unknown>(board.meta)[NOTE_LOOK_KEY];
  const isDefault = sameNoteLook(lookOfNote(item), parseNoteLook(stored));
  return (
    <>
      <div className="insp-label">Paper</div>
      <div className="swatches">
        {NOTE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`swatch note-${c}${item.props.color === c ? ' is-active' : ''}`}
            title={c}
            aria-label={c}
            onClick={() => board.updateItem(item.id, { props: { color: c } })}
          />
        ))}
        <label
          className={`swatch swatch-custom${isCustomPaper(item.props.color) ? ' is-active' : ''}`}
          title="Custom paper colour"
          style={isCustomPaper(item.props.color) ? { background: item.props.color } : undefined}
        >
          <input
            type="color"
            aria-label="Custom paper colour"
            value={isCustomPaper(item.props.color) ? item.props.color : '#f2e6d0'}
            onChange={(e) => board.updateItem(item.id, { props: { color: e.target.value as `#${string}` } })}
          />
        </label>
      </div>
      <TextStyleFields
        value={textStyleOf(item)}
        onChange={(patch) => board.updateItem(item.id, { props: textStylePatch('note', patch) })}
        defaultColorLabel="ink"
      />
      <div className="insp-label">Board default</div>
      <div className="btn-row">
        <button
          type="button"
          className="btn"
          disabled={isDefault}
          title="New sticky notes on this board get this note's paper, text style and size"
          onClick={(e) => {
            setBoardNoteLook(board, item);
            flash(e.currentTarget, 'New sticky notes will look like this one');
          }}
        >
          Set as default
        </button>
        {stored != null && (
          <button
            type="button"
            className="btn"
            title="New sticky notes go back to plain yellow squares"
            onClick={(e) => {
              resetBoardNoteLook(board);
              flash(e.currentTarget, 'New sticky notes are plain yellow again');
            }}
          >
            Reset
          </button>
        )}
      </div>
      {isDefault && <div className="insp-muted">New notes on this board look like this one.</div>}
    </>
  );
}

function TitleFields({ item }: { item: TitleItem }) {
  const board = useBoard();
  return (
    <TextStyleFields
      value={textStyleOf(item)}
      onChange={(patch) => board.updateItem(item.id, { props: textStylePatch('title', patch) })}
    />
  );
}

function ImageFields({ item }: { item: ImageItem }) {
  const board = useBoard();
  const pane = usePaneStore();
  const resetAspect = () => {
    const ratio = item.props.naturalW / Math.max(1, item.props.naturalH);
    board.updateItem(item.id, { h: Math.round(item.w / ratio) });
  };
  return (
    <>
      <div className="insp-label">Opacity</div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={item.props.opacity}
        onChange={(e) => board.updateItem(item.id, { props: { opacity: Number(e.target.value) } })}
      />
      <div className="btn-row">
        <button type="button" className="btn" onClick={resetAspect}>
          Reset aspect
        </button>
        <button type="button" className="btn" title="Cut a piece out of this image with a lasso, rectangle or ellipse (C)" onClick={() => pane.getState().setTool('cutout')}>
          <IconScissors /> Cut out
        </button>
      </div>
      <div className="insp-muted">
        {item.props.naturalW} × {item.props.naturalH} px
      </div>
    </>
  );
}

function ShapeFields({ item }: { item: ShapeItem }) {
  const board = useBoard();
  const p = item.props;
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  const isLine = p.kind === 'line' || p.kind === 'arrow';
  return (
    <>
      <div className="btn-row">
        {(['rect', 'ellipse', 'line', 'arrow'] as ShapeKind[]).map((k) => (
          <button key={k} type="button" className={`btn${p.kind === k ? ' is-active' : ''}`} onClick={() => set({ kind: k })}>
            {k}
          </button>
        ))}
      </div>
      {!isLine && (
        <>
          <div className="insp-label">Fill</div>
          <ColorPicker value={p.fill} onChange={(fill) => set({ fill })} allowNone />
        </>
      )}
      <div className="insp-label">Stroke</div>
      <ColorPicker value={p.stroke} onChange={(stroke) => set({ stroke })} allowNone={!isLine} />
      <SizePicker value={p.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => set({ strokeWidth })} />
      {isLine && (
        <button type="button" className="btn" onClick={() => set({ flip: !p.flip })}>
          Flip direction
        </button>
      )}
    </>
  );
}

function FrameFields({ item }: { item: FrameItem }) {
  const board = useBoard();
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  return (
    <>
      <div className="insp-label">Title</div>
      <input
        className="insp-input"
        value={item.props.title}
        placeholder="Frame"
        onChange={(e) => set({ title: e.target.value })}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div className="insp-label">Colour</div>
      <ColorPicker value={item.props.color ?? 'none'} onChange={(c) => set({ color: c === 'none' ? null : c })} allowNone />
      <div className="insp-muted">Items whose centre is inside the frame move with it.</div>
    </>
  );
}

const LINE_TOOLS: DrawTool[] = ['pen', 'fineliner', 'brush', 'highlighter'];

/** Ink: one stroke or a lasso selection of them (the fields show the first; changes apply to all). */
function StrokeFields({ items }: { items: StrokeItem[] }) {
  const board = useBoard();
  const first = items[0]!;
  const lines = items.filter((i) => i.props.tool !== 'fill');
  const set = (targets: StrokeItem[], props: Record<string, unknown>) => board.updateItems(targets.map((i) => [i.id, { props }]));
  return (
    <>
      <div className="insp-label">Colour</div>
      <ColorPicker value={first.props.color} onChange={(color) => set(items, { color })} />
      <OpacitySlider value={first.props.opacity ?? 1} onChange={(opacity) => set(items, { opacity })} />
      {lines.length > 0 && (
        <>
          <div className="insp-label">Width</div>
          <SizePicker value={lines[0]!.props.size} options={[1, 2, 4, 8, 14, 24, 40]} onChange={(size) => set(lines, { size })} />
          <div className="insp-label">Tool</div>
          <div className="btn-row">
            {LINE_TOOLS.map((t) => (
              <button
                key={t}
                type="button"
                className={`btn${lines.every((i) => i.props.tool === t) ? ' is-active' : ''}`}
                onClick={() => set(lines, { tool: t })}
              >
                {DRAW_TOOL_LABEL[t]}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function RichTextFields({ item }: { item: RichTextItem | TableItem | TodoItem }) {
  const board = useBoard();
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  return (
    <>
      <div className="insp-label">Background</div>
      <ColorPicker value={item.props.bg ?? 'none'} onChange={(bg) => set({ bg: bg === 'none' ? null : bg })} allowNone />
      <TextStyleFields
        value={textStyleOf(item)}
        onChange={(patch) => set(textStylePatch('richtext', patch))}
        showBoldItalic={false}
      />
      <div className="insp-muted">Select text while editing for inline formatting.</div>
    </>
  );
}

function FileFields({ item }: { item: FileItem }) {
  return (
    <>
      <div className="insp-label">Attachment</div>
      <div className="insp-url">
        {item.props.name} · {formatBytes(item.props.size)}
      </div>
      <div className="btn-row">
        <button type="button" className="btn" onClick={() => void openAsset(item.props.assetId, item.props.name)}>
          <IconExternal /> Download
        </button>
      </div>
    </>
  );
}

function LinkFields({ item }: { item: LinkItem }) {
  const board = useBoard();
  return (
    <>
      <div className="insp-label">URL</div>
      <div className="insp-url" title={item.props.url}>
        {item.props.url}
      </div>
      <div className="btn-row">
        <a className="btn" href={item.props.url} target="_blank" rel="noopener noreferrer">
          <IconExternal /> Open
        </a>
        <button type="button" className="btn" onClick={() => void refreshLinkPreview(board, item.id)}>
          Refresh preview
        </button>
      </div>
    </>
  );
}

function BoardCardFields({ item }: { item: BoardCardItem }) {
  const board = useBoard();
  const { setPanel } = usePanel();
  const p = item.props;
  const lookup = useBoardLookup(p.boardId);
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  const self = p.boardId === board.boardId;
  const boardName = lookup.board?.title ?? (lookup.status === 'missing' ? 'Not accessible' : lookup.status === 'loading' ? 'Loading…' : 'Unavailable');
  const coverMode = p.coverAssetId ? 'image' : p.showPreview ? 'preview' : 'none';

  const uploadCover = async () => {
    const [file] = await pickImageFiles();
    if (!file || !isImageFile(file)) return;
    const maxSide = board.meta.get('importMaxSide');
    const meta = await storeImageAsset(file, board.boardId, { maxSide: typeof maxSide === 'number' ? maxSide : 0 });
    if (!board.index.has(item.id)) return;
    set({ coverAssetId: meta.id });
    if (board.remote) void enqueueUpload(board.boardId, meta.id);
  };

  return (
    <>
      <div className="insp-label">Board</div>
      <div className="insp-url" title={p.boardId}>
        <IconBoard /> {self ? 'This board' : boardName}
      </div>
      <div className="btn-row">
        <button type="button" className="btn" disabled={self || lookup.status === 'missing'} title="Open the board (double-click the card)" onClick={() => void openBoardCard(board, item)}>
          <IconExternal /> Open
        </button>
        <button type="button" className="btn" disabled={self || lookup.status === 'missing'} title="Open the board next to this one" onClick={() => void openBoardCard(board, item, true)}>
          Split
        </button>
        <button
          type="button"
          className="btn"
          title="Point this card at another board"
          onClick={() => {
            setBoardPickerTarget(item.id);
            setPanel('boards');
          }}
        >
          Change…
        </button>
      </div>
      <div className="insp-label">Title</div>
      <input
        className="insp-input"
        value={p.title}
        placeholder={lookup.board?.title ?? 'Board title'}
        onChange={(e) => set({ title: e.target.value })}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div className="insp-label">Description</div>
      <textarea
        className="insp-input insp-textarea"
        rows={3}
        value={p.description}
        placeholder="Optional"
        onChange={(e) => set({ description: e.target.value })}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div className="insp-label">Cover</div>
      <div className="btn-row">
        <button type="button" className={`btn${coverMode === 'preview' ? ' is-active' : ''}`} title="The board's own preview, kept up to date" onClick={() => set({ coverAssetId: null, showPreview: true })}>
          Preview
        </button>
        <button type="button" className={`btn${coverMode === 'image' ? ' is-active' : ''}`} title="Upload a cover image" onClick={() => void uploadCover()}>
          Image…
        </button>
        <button type="button" className={`btn${coverMode === 'none' ? ' is-active' : ''}`} title="No picture" onClick={() => set({ coverAssetId: null, showPreview: false })}>
          None
        </button>
      </div>
      <div className="insp-label">Layout</div>
      <div className="btn-row">
        {(['card', 'poster'] as BoardCardLayout[]).map((l) => (
          <button key={l} type="button" className={`btn${p.layout === l ? ' is-active' : ''}`} onClick={() => set({ layout: l })}>
            {l}
          </button>
        ))}
        {(['cover', 'contain'] as BoardCardFit[]).map((f) => (
          <button key={f} type="button" className={`btn${p.fit === f ? ' is-active' : ''}`} title={f === 'cover' ? 'Fill the cover area' : 'Show the whole picture'} onClick={() => set({ fit: f })}>
            {f === 'cover' ? 'fill' : 'fit'}
          </button>
        ))}
      </div>
      <div className="insp-label">Colour</div>
      <ColorPicker value={p.color ?? 'none'} onChange={(c) => set({ color: c === 'none' ? null : c })} allowNone />
    </>
  );
}

function SheetFields({ item }: { item: SheetItem }) {
  const board = useBoard();
  const pane = usePaneStore();
  const { setPanel } = usePanel();
  const meta = useBoardMeta();
  const ctx = { board, pane };
  const set = (props: Record<string, unknown>) => board.updateItem(item.id, { props });
  const layerCount = Object.keys(item.props.layers).length;
  const resetAspect = () => board.updateItem(item.id, { h: Math.round((item.w * item.props.pxH) / Math.max(1, item.props.pxW)) });
  return (
    <>
      <div className="insp-label">Sketch sheet</div>
      <div className="insp-muted">
        {sheetLabel(item.props)} · {layerCount} {layerCount === 1 ? 'layer' : 'layers'}
      </div>
      <div className="btn-row">
        <button type="button" className="btn" title="Paint on this sheet (double-click it, or Enter)" disabled={board.isLocked(item)} onClick={() => enterPaint(ctx, item.id)}>
          <IconPen /> Paint
        </button>
        <button type="button" className="btn" title="Its layer stack" onClick={() => setPanel('sheet')}>
          <IconLayers /> Layers…
        </button>
      </div>
      <div className="insp-label">Paper</div>
      <ColorPicker value={item.props.paper ?? 'none'} onChange={(c) => set({ paper: c === 'none' ? null : c })} allowNone />
      <div className="btn-row">
        <button type="button" className="btn" title="Match the box to the pixel proportions" onClick={resetAspect}>
          Reset aspect
        </button>
      </div>
      <div className="btn-row">
        <button type="button" className="btn" title="An ordinary image of every visible layer, placed next to the sheet" onClick={() => void flattenSheet(ctx, item.id)}>
          Flatten to image
        </button>
        <button type="button" className="btn" title="Download the sheet as a PNG at full pixel size" onClick={() => void exportSheetPng(board, item.id, meta.title)}>
          Export PNG
        </button>
      </div>
    </>
  );
}

function NumberField({
  label,
  value,
  onCommit,
  min,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onCommit: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  const [text, setText] = useState(String(Math.round(value * 100) / 100));
  useEffect(() => setText(String(Math.round(value * 100) / 100)), [value]);
  const commit = () => {
    const n = Number(text);
    if (!Number.isFinite(n)) return setText(String(value));
    const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        value={text}
        step={step}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          e.stopPropagation();
        }}
      />
    </label>
  );
}
